import React, { useState, useCallback, useMemo } from 'react';
import { Loading } from '@components/atoms/Loading';
import { Button } from '@components/atoms/Button';
import { Input } from '@components/atoms/Input';
import { Tab } from '@components/atoms/Tab';
import ReactSelect, { SelectOption } from '@components/atoms/ReactSelect';
import { getCategoryIcon } from '@utils/categoryIcons';
import { SingleValue, MultiValue } from 'react-select';
import { CategoryFormModal } from '@components/organisms/CategoryFormModal';
import { ConfirmationModal } from '@components/molecules/ConfirmationModal';
import {
  useGlobalMaterialCategories,
  useCreateGlobalMaterialCategory,
  useUpdateGlobalMaterialCategory,
  useDeleteGlobalMaterialCategory,
} from '@hooks/api/warehouse';
import {
  MaterialCategory,
  MaterialCategoryCreateInput,
  MaterialCategoryUpdateInput,
  CategoryType,
} from '@models/warehouse.model';
import AddIcon from '@mui/icons-material/Add';
import CategoryIcon from '@mui/icons-material/Category';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import BusinessIcon from '@mui/icons-material/Business';
import { toast } from 'react-toastify';
import { useTranslation } from '@hooks/useTranslation';

type FilterTab = 'material' | 'equipment';
type StatusFilter = 'all' | 'active' | 'inactive';

const COLOR_MAP: Record<string, string> = {
  blue: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
  purple: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
  orange: 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400',
  cyan: 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400',
  yellow: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400',
  green: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400',
  indigo: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400',
};

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All Status' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

const SuperAdminCategoriesPage: React.FC = () => {
  const tActionMessages = useTranslation('actionMessages');
  const [activeTab, setActiveTab] = useState<FilterTab>('material');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<MaterialCategory | null>(null);
  const [categoryToDelete, setCategoryToDelete] = useState<MaterialCategory | null>(null);

  // API Hooks
  const {
    data: categories = [],
    isLoading: categoriesLoading,
    isError: categoriesError,
  } = useGlobalMaterialCategories();

  // Mutations
  const createMutation = useCreateGlobalMaterialCategory();
  const updateMutation = useUpdateGlobalMaterialCategory();
  const deleteMutation = useDeleteGlobalMaterialCategory();

  const isLoading = categoriesLoading && !categoriesError;

  // Filter categories by tab, status, and search
  const filteredCategories = useMemo(() => {
    let filtered = categories;

    // Tab filter
    if (activeTab === 'material') {
      filtered = filtered.filter(
        (c) => c.type === CategoryType.MATERIAL || c.type === CategoryType.BOTH
      );
    } else {
      filtered = filtered.filter(
        (c) => c.type === CategoryType.EQUIPMENT || c.type === CategoryType.BOTH
      );
    }

    // Status filter
    if (statusFilter === 'active') {
      filtered = filtered.filter((c) => c.isActive);
    } else if (statusFilter === 'inactive') {
      filtered = filtered.filter((c) => !c.isActive);
    }

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (c) =>
          c.name.toLowerCase().includes(query) ||
          c.description?.toLowerCase().includes(query)
      );
    }

    return filtered.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
  }, [categories, activeTab, statusFilter, searchQuery]);

  // Stats
  const materialCount = categories.filter(
    (c) => c.type === CategoryType.MATERIAL || c.type === CategoryType.BOTH
  ).length;
  const equipmentCount = categories.filter(
    (c) => c.type === CategoryType.EQUIPMENT || c.type === CategoryType.BOTH
  ).length;
  const activeCount = categories.filter((c) => c.isActive).length;

  // Handlers
  const handleAddCategory = useCallback(() => {
    setSelectedCategory(null);
    setIsFormModalOpen(true);
  }, []);

  const handleEditCategory = useCallback((category: MaterialCategory) => {
    setSelectedCategory(category);
    setIsFormModalOpen(true);
  }, []);

  const handleCloseFormModal = useCallback(() => {
    setIsFormModalOpen(false);
    setSelectedCategory(null);
  }, []);

  const handleSubmitForm = useCallback(
    async (data: MaterialCategoryCreateInput | MaterialCategoryUpdateInput) => {
      try {
        if ('categoryId' in data && data.categoryId) {
          await updateMutation.mutateAsync(data as MaterialCategoryUpdateInput);
          toast.success(tActionMessages.categoryUpdatedSuccessfully);
        } else {
          await createMutation.mutateAsync(data as MaterialCategoryCreateInput);
          toast.success(tActionMessages.categoryCreatedSuccessfully);
        }
        handleCloseFormModal();
      } catch (error) {
        console.error('Error saving category:', error);
        toast.error(tActionMessages.failedToSaveCategory);
      }
    },
    [
      createMutation,
      updateMutation,
      handleCloseFormModal,
      tActionMessages.categoryCreatedSuccessfully,
      tActionMessages.categoryUpdatedSuccessfully,
      tActionMessages.failedToSaveCategory,
    ]
  );

  const handleDeleteCategory = useCallback((category: MaterialCategory) => {
    setCategoryToDelete(category);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!categoryToDelete) return;
    try {
      await deleteMutation.mutateAsync(categoryToDelete.categoryId);
      toast.success(tActionMessages.categoryDeletedSuccessfully);
      setCategoryToDelete(null);
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error(tActionMessages.failedToDeleteCategory);
    }
  }, [
    categoryToDelete,
    deleteMutation,
    tActionMessages.categoryDeletedSuccessfully,
    tActionMessages.failedToDeleteCategory,
  ]);

  const handleCancelDelete = useCallback(() => {
    setCategoryToDelete(null);
  }, []);

  // Generate slug from name
  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <Loading size="2xl" center />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <AdminPanelSettingsIcon className="text-indigo-600 dark:text-indigo-400" sx={{ fontSize: 28 }} />
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Material & Equipment Categories
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Manage global categories for all organizations</p>
          </div>
        </div>
        <Button
          variant="primary"
          leftIcon={<AddIcon sx={{ fontSize: 16 }} />}
          onClick={handleAddCategory}
        >
          Add Category
        </Button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm ring-1 ring-gray-900/5 dark:ring-gray-700 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Total Categories</p>
              <p className="text-xl font-semibold text-gray-900 dark:text-white">{categories.length}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">in platform</p>
            </div>
            <div className="w-12 h-12 bg-indigo-100 dark:bg-indigo-900/30 rounded-xl flex items-center justify-center">
              <CategoryIcon className="text-indigo-600 dark:text-indigo-400 text-2xl" />
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm ring-1 ring-gray-900/5 dark:ring-gray-700 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Active</p>
              <p className="text-xl font-semibold text-gray-900 dark:text-white">{activeCount}</p>
              <p className="text-xs text-green-600 dark:text-green-400 mt-1">available to orgs</p>
            </div>
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-xl flex items-center justify-center">
              <CheckCircleIcon className="text-green-600 dark:text-green-400 text-2xl" />
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm ring-1 ring-gray-900/5 dark:ring-gray-700 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Inactive</p>
              <p className="text-xl font-semibold text-gray-900 dark:text-white">{categories.length - activeCount}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">hidden from orgs</p>
            </div>
            <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-xl flex items-center justify-center">
              <BusinessIcon className="text-gray-500 dark:text-gray-400 text-2xl" />
            </div>
          </div>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-700 rounded-lg p-4">
        <div className="flex items-start gap-2">
          <InfoOutlinedIcon className="text-indigo-500 dark:text-indigo-400 mt-0.5" sx={{ fontSize: 18 }} />
          <div>
            <p className="text-sm font-medium text-indigo-800 dark:text-indigo-300">Global Categories</p>
            <p className="text-sm text-indigo-700 dark:text-indigo-400 mt-1">
              These categories are available to all organizations. Changes here will affect the category options across the entire platform.
            </p>
          </div>
        </div>
      </div>

      {/* Tabs + Search & Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm ring-1 ring-gray-900/5 dark:ring-gray-700">
        <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between flex-wrap gap-4">
          <Tab
            tabs={[
              { id: 'material', name: `Material (${materialCount})` },
              { id: 'equipment', name: `Equipment (${equipmentCount})` },
            ]}
            activeTab={activeTab}
            onTabChange={(tabId) => setActiveTab(tabId as FilterTab)}
            variant="pills"
          />
          <div className="flex items-center gap-3">
            <div className="w-64">
              <Input
                placeholder="Search categories..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                iconLeft={<SearchIcon sx={{ fontSize: 18 }} />}
              />
            </div>
            <div className="w-40">
              <ReactSelect
                options={STATUS_OPTIONS.map((opt) => ({
                  value: opt.value,
                  label: opt.label,
                }))}
                value={{
                  value: statusFilter,
                  label: STATUS_OPTIONS.find((o) => o.value === statusFilter)?.label || 'All Status',
                }}
                onChange={(option: SingleValue<SelectOption> | MultiValue<SelectOption>) => {
                  if (option && !Array.isArray(option) && 'value' in option) {
                    setStatusFilter(option.value as StatusFilter);
                  }
                }}
                placeholder="All Status"
                isClearable={false}
              />
            </div>
          </div>
        </div>

        {/* Categories Grid */}
        <div className="p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredCategories.map((cat) => {
              const colorClasses = COLOR_MAP[cat.color || 'blue'] || COLOR_MAP.blue;

              return (
                <div
                  key={cat.categoryId}
                  className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm ring-1 ring-gray-900/5 dark:ring-gray-700 p-4 relative group transition-all hover:shadow-md ${
                    !cat.isActive ? 'opacity-60' : ''
                  }`}
                >
                  {/* Icon */}
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${colorClasses}`}>
                    {getCategoryIcon(cat.icon, { sx: { fontSize: 22 } })}
                  </div>

                  {/* Name & Description */}
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{cat.name}</h3>
                  {cat.description && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">{cat.description}</p>
                  )}

                  {/* Slug */}
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 font-mono">{generateSlug(cat.name)}</p>

                  {/* Status & Type */}
                  <div className="flex items-center gap-2 mt-3">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        cat.isActive
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                      }`}
                    >
                      {cat.isActive ? 'Active' : 'Inactive'}
                    </span>
                    <span className="text-xs text-gray-400 dark:text-gray-500">
                      {cat.type === CategoryType.BOTH ? 'Material & Equipment' : cat.type}
                    </span>
                  </div>

                  {/* Hover Actions */}
                  <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                    <button type="button"
                      onClick={() => handleEditCategory(cat)}
                      className="p-1.5 bg-white dark:bg-gray-700 rounded-md shadow-sm ring-1 ring-gray-200 dark:ring-gray-600 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 hover:ring-indigo-200 dark:hover:ring-indigo-600"
                      title="Edit"
                    >
                      <EditIcon sx={{ fontSize: 16 }} />
                    </button>
                    <button type="button"
                      onClick={() => handleDeleteCategory(cat)}
                      className="p-1.5 bg-white dark:bg-gray-700 rounded-md shadow-sm ring-1 ring-gray-200 dark:ring-gray-600 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:ring-red-200 dark:hover:ring-red-600"
                      title="Delete"
                    >
                      <DeleteIcon sx={{ fontSize: 16 }} />
                    </button>
                  </div>
                </div>
              );
            })}

            {/* Add Category Card */}
            <button type="button"
              onClick={handleAddCategory}
              className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4 flex flex-col items-center justify-center gap-2 text-gray-400 dark:text-gray-500 hover:text-indigo-600 dark:hover:text-indigo-400 hover:border-indigo-300 dark:hover:border-indigo-600 transition-colors min-h-[140px]"
            >
              <AddIcon sx={{ fontSize: 24 }} />
              <span className="text-sm font-medium">Add Category</span>
            </button>
          </div>
        </div>
      </div>

      {/* Form Modal */}
      <CategoryFormModal
        isOpen={isFormModalOpen}
        onClose={handleCloseFormModal}
        onSubmit={handleSubmitForm}
        category={selectedCategory}
        isLoading={createMutation.isPending || updateMutation.isPending}
      />

      {/* Delete Confirmation */}
      <ConfirmationModal
        isOpen={!!categoryToDelete}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete Category"
        description={`Are you sure you want to delete "${categoryToDelete?.name}"? This will affect all organizations using this category. This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
      />
    </div>
  );
};

export default SuperAdminCategoriesPage;
